/*
* 利率計算UIコントローラ
*/
import InterestRateCalculator from './calculator';

export default class CalculateInterestRate {
  constructor() {
    // 固定DOM要素をキャッシュ
    this.elements = {
      displayOperationTerm: document.getElementById('displayOperationTerm'),
      displayOperationInterestRate: document.getElementById('displayOperationInterestRate'),
      displayDividendSection: document.getElementById('displayDividendSection'),
      operationStartDate: document.getElementById('operationStartDate'),
      operationEndDate: document.getElementById('operationEndDate'),
      interestRate: document.getElementById('interestRate'),
      investmentBasedRadio: document.querySelector('input[name="operation_result[tk_calculation_method]"][value="investment_based"]'),
      perUnitBasedRadio: document.querySelector('input[name="operation_result[tk_calculation_method]"][value="per_unit_based"]'),
      tkCalculationMethodHidden: document.getElementById('operation_result_tk_calculation_method'),
    };
  }

  /**
   * 現在の計算方式を取得
   * @returns {string} 'per_unit_based' | 'investment_based'
   */
  getCurrentCalculationMethod() {
    // 新規作成時：ラジオボタンをチェック
    if (this.elements.perUnitBasedRadio?.checked) {
      return 'per_unit_based';
    }
    // 編集時：hidden_fieldをチェック
    if (this.elements.tkCalculationMethodHidden?.value === 'per_unit_based') {
      return 'per_unit_based';
    }
    return 'investment_based';
  }

  /**
   * 現在の配当値要素を取得（動的に変わるため都度取得）
   * @returns {HTMLElement|null}
   */
  getCurrentDividendElement() {
    const method = this.getCurrentCalculationMethod();
    return this.elements.displayDividendSection?.querySelector(
      `[data-for="${method}"] .dividend-value`
    );
  }

  /**
   * 1口あたりの金額を取得
   * @returns {number}
   */
  getUnitPrice() {
    const dividendElement = this.getCurrentDividendElement();
    return dividendElement ? parseInt(dividendElement.dataset.unitPrice, 10) || 10000 : 10000;
  }

  /**
   * 運用日数を計算して表示
   */
  updateOperationTerm() {
    const term = InterestRateCalculator.calculateOperationTerm(
      this.elements.operationStartDate.value,
      this.elements.operationEndDate.value
    );
    // 日付未入力の場合は「-」を表示
    this.elements.displayOperationTerm.textContent = term > 0 ? term : '-';
  }

  /**
   * 運用期間での利率を計算して表示
   */
  updateOperationInterestRate() {
    const interestRateValue = parseFloat(this.elements.interestRate.value);
    const termText = this.elements.displayOperationTerm.textContent;

    // 確定利率または運用日数が未入力の場合は「-」を表示
    if (Number.isNaN(interestRateValue) || termText === '-') {
      this.elements.displayOperationInterestRate.textContent = '-';
      return;
    }

    const rate = InterestRateCalculator.calculateOperationInterestRate(
      interestRateValue,
      parseFloat(termText)
    );
    this.elements.displayOperationInterestRate.textContent = rate.toLocaleString();
  }

  /**
   * 配当金額を計算して表示
   */
  updateDividend() {
    const dividendElement = this.getCurrentDividendElement();
    if (!dividendElement) return;

    const rateText = this.elements.displayOperationInterestRate.textContent;
    // 運用期間での利率が「-」の場合は配当も「-」
    if (rateText === '-') {
      dividendElement.textContent = '-';
      return;
    }

    const dividend = InterestRateCalculator.calculateDividend(
      rateText,
      this.getCurrentCalculationMethod(),
      this.getUnitPrice()
    );
    dividendElement.textContent = Number.isFinite(dividend) ? dividend.toLocaleString() : '-';
  }

  /**
   * 配当表示の切り替え
   */
  updateDividendDisplay() {
    if (!this.elements.displayDividendSection) return;

    const method = this.getCurrentCalculationMethod();
    const investmentDiv = this.elements.displayDividendSection.querySelector('[data-for="investment_based"]');
    const perUnitDiv = this.elements.displayDividendSection.querySelector('[data-for="per_unit_based"]');

    if (method === 'per_unit_based') {
      investmentDiv.style.display = 'none';
      investmentDiv.setAttribute('aria-hidden', 'true');
      perUnitDiv.style.display = 'block';
      perUnitDiv.setAttribute('aria-hidden', 'false');
    } else {
      investmentDiv.style.display = 'block';
      investmentDiv.setAttribute('aria-hidden', 'false');
      perUnitDiv.style.display = 'none';
      perUnitDiv.setAttribute('aria-hidden', 'true');
    }
  }

  /**
   * すべての計算を更新
   */
  updateAllCalculations() {
    this.updateOperationTerm();
    this.updateOperationInterestRate();
    this.updateDividend();
  }

  /**
   * 日付変更時のイベントハンドラー
   */
  handleDateChange = () => {
    this.updateAllCalculations();
  };

  /**
   * 利率変更時のイベントハンドラー
   */
  handleInterestRateChange = () => {
    this.updateOperationInterestRate();
    this.updateDividend();
  };

  /**
   * 計算方式変更時のイベントハンドラー
   */
  handleCalculationMethodChange = () => {
    this.updateDividendDisplay();
    this.updateDividend();
  };

  /**
   * 初期表示の設定
   */
  init() {
    this.updateDividendDisplay();
    this.updateAllCalculations();
  }

  /**
   * イベントリスナーを登録
   */
  attachEvent() {
    if (!this.elements.displayOperationTerm) return;

    // ページ表示時の初期化
    window.addEventListener('pageshow', () => this.init());

    // 日付変更時
    [this.elements.operationStartDate, this.elements.operationEndDate].forEach(el => {
      el?.addEventListener('change', this.handleDateChange);
    });

    // 確定利回り変更時（リアルタイム更新 + フォーカスアウト時）
    this.elements.interestRate?.addEventListener('input', this.handleInterestRateChange);
    this.elements.interestRate?.addEventListener('change', this.handleInterestRateChange);

    // 計算方式変更時
    [this.elements.investmentBasedRadio, this.elements.perUnitBasedRadio]
      .filter(Boolean)
      .forEach(radio => {
        radio.addEventListener('change', this.handleCalculationMethodChange);
      });
  }
}
